import type {
  ContactPhoneCall,
  ContactPhoneCallCreateInput,
  ContactPhoneCallParticipant,
} from "@/lib/types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export type CallOutcome = ContactPhoneCall["outcome"];
export type CallDirection = ContactPhoneCall["direction"];

export const OUTCOME_OPTIONS: { label: string; value: CallOutcome }[] = [
  { label: "Completed", value: "COMPLETED" },
  { label: "Partially completed", value: "PARTIALLY_COMPLETED" },
  { label: "Missed", value: "MISSED" },
  { label: "No answer", value: "NO_ANSWER" },
  { label: "Busy", value: "BUSY" },
  { label: "Declined", value: "DECLINED" },
  { label: "Voicemail", value: "VOICEMAIL" },
  { label: "Abandoned", value: "ABANDONED" },
  { label: "Canceled", value: "CANCELED" },
  { label: "Failed", value: "FAILED" },
];

export const outcomeLabel = (outcome: CallOutcome) =>
  OUTCOME_OPTIONS.find((option) => option.value === outcome)?.label ?? outcome;

export const outcomeVariant = (outcome: CallOutcome) => {
  if (outcome === "COMPLETED") return "success" as const;
  if (outcome === "PARTIALLY_COMPLETED" || outcome === "VOICEMAIL")
    return "warning" as const;
  if (outcome === "FAILED" || outcome === "MISSED")
    return "destructive" as const;
  return "secondary" as const;
};

export const parsePhoneCallUtcDate = (value: string | Date) =>
  value instanceof Date ? value : dayjs.utc(value).toDate();

export const formatDuration = (startedAt: string, endedAt: string) => {
  const durationMs = Math.max(
    0,
    parsePhoneCallUtcDate(endedAt).getTime() -
      parsePhoneCallUtcDate(startedAt).getTime(),
  );
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = Math.floor((durationMs % 60_000) / 1_000);
  if (minutes >= 60) {
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  }
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

export const toDateTimeLocalValue = (value: string | Date) => {
  const date = parsePhoneCallUtcDate(value);
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

export type EditableParticipant = {
  key: string;
  kind: "USER" | "FIRM_PHONE" | "EXTERNAL" | "CONTACT";
  value: string;
  phoneNumber: string;
  joinedAt?: string | null;
  leftAt?: string | null;
  displayLabel?: string;
};

export const participantToEditable = (
  participant: ContactPhoneCallParticipant,
): EditableParticipant => ({
  key: participant.id,
  kind: participant.kind,
  value:
    participant.userId ??
    participant.firmPhoneId ??
    participant.contactId ??
    "",
  phoneNumber: participant.phoneNumber ?? "",
  joinedAt: participant.joinedAt,
  leftAt: participant.leftAt,
});

export const newParticipant = (): EditableParticipant => ({
  key: crypto.randomUUID(),
  kind: "EXTERNAL",
  value: "",
  phoneNumber: "",
});

type ParticipantInput = ContactPhoneCallCreateInput["participants"][number];

export const buildParticipant = (
  participant: EditableParticipant,
): ParticipantInput | null => {
  const interval = {
    ...(participant.joinedAt ? { joinedAt: participant.joinedAt } : {}),
    ...(participant.leftAt ? { leftAt: participant.leftAt } : {}),
  };
  if (participant.kind === "USER") {
    return participant.value
      ? {
          kind: "USER",
          userId: participant.value,
          ...(participant.phoneNumber
            ? { phoneNumber: participant.phoneNumber }
            : {}),
          ...interval,
        }
      : null;
  }
  if (participant.kind === "FIRM_PHONE") {
    return participant.value || participant.phoneNumber
      ? {
          kind: "FIRM_PHONE",
          ...(participant.value ? { firmPhoneId: participant.value } : {}),
          ...(participant.phoneNumber
            ? { phoneNumber: participant.phoneNumber }
            : {}),
          ...interval,
        }
      : null;
  }
  if (participant.kind === "CONTACT") {
    return participant.value
      ? {
          kind: "CONTACT",
          contactId: participant.value,
          ...(participant.phoneNumber
            ? { phoneNumber: participant.phoneNumber }
            : {}),
          ...interval,
        }
      : null;
  }
  return participant.phoneNumber.trim()
    ? {
        kind: "EXTERNAL",
        phoneNumber: participant.phoneNumber.trim(),
        ...interval,
      }
    : null;
};
